import { computed, inject, Injectable } from '@angular/core';
import { Locator, LocatorLocations, LocatorText } from '@readium/shared';
import type { Progress100 } from '../../models/selections';
import { MetadataService } from '../metadata/metadata';

interface ProgressRange {
  start: Progress100;
  end: Progress100;
}

const FULL_PROGRESS = 100;

function clampProgression(progression: number): number {
  return Math.max(0, Math.min(1, progression));
}

/**
 * Book percentages weight each spine item by its byte size, exactly like the legacy reader did,
 * so the bookmarks, annotations and questions it saved resolve to the same place here.
 */
@Injectable({
  providedIn: 'root',
})
export class BookAnalysisService {
  private readonly metadataService = inject(MetadataService);

  /**
   * Range of every spine item, `linear="no"` ones included: they are missing from the
   * readingOrder but still weigh on the book total, as they did in the legacy reader.
   */
  private readonly spineRanges = computed<Map<string, ProgressRange>>(() => {
    const ranges = new Map<string, ProgressRange>();
    const metadata = this.metadataService.metadata();
    if (!metadata) return ranges;

    const sizes = metadata.spine.map((_, index) => metadata.properties[index]?.size ?? 0);
    const total = sizes.reduce((sum, size) => sum + size, 0);
    if (!total) return ranges;

    let accumulated = 0;
    metadata.spine.forEach((href, index) => {
      ranges.set(href, {
        start: (accumulated / total) * FULL_PROGRESS,
        end: ((accumulated + sizes[index]) / total) * FULL_PROGRESS,
      });
      accumulated += sizes[index];
    });
    return ranges;
  });

  /**
   * Range of each position. A chapter ends where its own bytes end, not where the next position
   * starts: a non-linear item in between must not stretch the chapter before it.
   */
  private readonly positionRanges = computed<ProgressRange[]>(() => {
    const positions = this.metadataService.positions();
    const spineRanges = this.spineRanges();
    return positions.map(
      (position, index) =>
        spineRanges.get(position.href) ?? {
          start: (position.locations.totalProgression ?? 0) * FULL_PROGRESS,
          end: (positions[index + 1]?.locations.totalProgression ?? 1) * FULL_PROGRESS,
        },
    );
  });

  getLocatorForPercentage(
    percentage: Progress100 | undefined,
    textLocator?: string,
  ): Locator | null {
    if (!this.metadataService.positions().length || percentage === undefined) return null;

    const positions = this.metadataService.positions();
    const ranges = this.positionRanges();
    // A percentage before the first position (e.g. on a non-linear cover) opens the book start.
    const positionIndex = Math.max(
      0,
      ranges.findLastIndex((range) => range.start <= percentage),
    );
    const position = positions[positionIndex];
    const { start, end } = ranges[positionIndex];

    const text = textLocator ? LocatorText.deserialize({ highlight: textLocator }) : undefined;
    return new Locator({
      href: position.href,
      type: position.type || 'application/xhtml+xml',
      locations: new LocatorLocations({
        position: position.locations.position,
        totalProgression: percentage / FULL_PROGRESS,
        progression: end > start ? clampProgression((percentage - start) / (end - start)) : 0,
      }),
      text,
    });
  }

  calculateProgressFromLocator(locator: Locator): Progress100 {
    const position = locator.locations.position ?? 0;
    const progression = locator.locations.progression ?? 0;
    return this.getProgressPercentage(position, progression);
  }

  private getProgressPercentage(position: number, localProgression = 0): Progress100 {
    const positionIndex = this.metadataService
      .positions()
      .findIndex((i) => i.locations.position === position);
    if (positionIndex === -1) return 0;

    const { start, end } = this.positionRanges()[positionIndex];
    return start + (end - start) * clampProgression(localProgression);
  }
}
